/**
 * Customer & product analytics (M3), mounted as `acp.*` through `extra.ts`.
 * `acpEn` is typed against the Polish shape, so a missing key is a compile error. Sections live in two files
 * (`analytics-cp-customers.ts`, `analytics-cp-products.ts`) to keep every file readable.
 */
import { acpCustomersEn, acpCustomersPl } from './analytics-cp-customers';
import { acpRiskEn, acpRiskPl } from './analytics-cp-risk';
import { acpProductsEn, acpProductsPl } from './analytics-cp-products';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

const sharedPl = {
  common: {
    all: 'Wszystkie',
    noData: 'Brak danych dla wybranych filtrów',
    daysShort: 'd',
    retry: 'Spróbuj ponownie',
    loadError: 'Nie udało się pobrać danych.',
    loadErrorHint: 'Sprawdź połączenie i spróbuj ponownie.',
    forbidden: 'Brak uprawnień do tej analizy.',
    openCustomer: 'Otwórz kartę klienta',
    vsPrev: 'vs poprzedni okres',
  },
  scope: {
    period: 'Analiza obejmuje cały wybrany okres.',
    asOf: 'Stan na {date} — z filtra okresu liczy się tylko data końcowa.',
    window12: 'Okno 12 miesięcy do {date} — z filtra okresu liczy się tylko data końcowa.',
    window24: 'Okno 24 miesięcy do {date} — z filtra okresu liczy się tylko data końcowa.',
    noGroup: 'Filtr grupy towarowej nie dotyczy tego widoku.',
    noSegmentRegion: 'Analiza koszyka pomija filtry segmentu i regionu.',
  },
  table: { customer: 'Klient', sales: 'Sprzedaż', marginPct: 'Marża %', delta: 'Zmiana' },
};

export const acpPl = { ...sharedPl, ...acpCustomersPl, ...acpRiskPl, ...acpProductsPl };
export type AcpMessages = typeof acpPl;
export type AcpKey = Leaves<AcpMessages>;

const sharedEn: Shape<typeof sharedPl> = {
  common: {
    all: 'All',
    noData: 'No data for the selected filters',
    daysShort: 'd',
    retry: 'Try again',
    loadError: 'Could not load the data.',
    loadErrorHint: 'Check your connection and try again.',
    forbidden: 'You are not allowed to view this analysis.',
    openCustomer: 'Open customer card',
    vsPrev: 'vs previous period',
  },
  scope: {
    period: 'The analysis covers the whole selected period.',
    asOf: 'As of {date} — only the period end date is used.',
    window12: '12-month window up to {date} — only the period end date is used.',
    window24: '24-month window up to {date} — only the period end date is used.',
    noGroup: 'The product group filter does not apply to this view.',
    noSegmentRegion: 'Basket analysis ignores the segment and region filters.',
  },
  table: { customer: 'Customer', sales: 'Sales', marginPct: 'Margin %', delta: 'Change' },
};

export const acpEn: Shape<AcpMessages> = { ...sharedEn, ...acpCustomersEn, ...acpRiskEn, ...acpProductsEn };
